import { startCase } from 'lodash'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { FC, useMemo } from 'react'

import { BlackOrbKing, WhiteOrbKing } from 'assets/game/orbs'

import { evaluatePosition } from 'features/ai/position-evaluation'
import { GameEventType } from 'features/firebase/types'

import { Button } from 'shared/components/ui/button'
import { isDev } from 'shared/config'
import { useKeyboardShortcuts } from 'shared/hooks/use-keyboard-shortcuts'

import { useGameContext } from '../game-context'
import { RequestDrawDialog } from './request-draw-dialog'
import { Resign } from './resign'

const gameEventsHumanNamesMap: Record<GameEventType, string> = {
  deploy: 'Deploy',
  distribute: 'Distribute',
  draw: 'Draw',
  fight: 'Fight',
  initialize: 'Initialize',
  move: 'Move',
  phaseTransition: 'Phase change',
  switch: 'Switch',
  withdraw: 'Pick Up',
}

export const Header: FC = () => {
  const { game, boardState, turn, replayEvent, prevMove, nextMove } = useGameContext()
  useKeyboardShortcuts({ left: prevMove, right: nextMove })

  const TurnOrb = turn === 'white' ? WhiteOrbKing : BlackOrbKing
  const winner = game.result?.winner
  const score = useMemo(() => evaluatePosition(boardState), [boardState])

  return (
    <div className="mx-3 my-2 flex flex-row justify-between sm:mx-10 lg:my-5">
      <Resign />
      <div className="flex items-center gap-2 text-xl font-bold lg:text-4xl">
        {(() => {
          if (game.status === 'pending') {
            return 'Looking for a player...'
          }

          if (game.status === 'finished') {
            return (
              <div className="flex flex-col items-center">
                <div className="flex gap-5">
                  {replayEvent !== null && gameEventsHumanNamesMap[game.events[replayEvent].type]}
                </div>
                <div>
                  <Button variant={'ghost'} onClick={prevMove}>
                    <ArrowLeft />
                  </Button>
                  {replayEvent && replayEvent} / {game.events.length - 1}
                  <Button variant={'ghost'} onClick={nextMove}>
                    <ArrowRight />
                  </Button>
                </div>
              </div>
            )
          }

          if (winner) {
            if (winner === 'draw') {
              return "It's a draw!"
            }
            return `${startCase(winner)} has won!`
          }

          return (
            <>
              {isDev ? score : null}
              <TurnOrb className="h-10 lg:h-[70px]" />
              {startCase(turn)}&apos;s turn
            </>
          )
        })()}
      </div>
      <RequestDrawDialog />
    </div>
  )
}
